"use client";

import type { ReactNode } from "react";

import { motion, useReducedMotion } from "motion/react";

/**
 * Entrance motion primitives.
 *
 * Every one of these collapses to a plain fade — or to nothing — when the user
 * has asked for reduced motion. That check lives here rather than in each
 * caller so a new animated section cannot forget it.
 *
 * Distances are small on purpose: 8–16px reads as the content settling into
 * place, while the 40px+ slides typical of template sites read as a page that
 * has not finished loading.
 */

const EASE = [0.22, 0.61, 0.36, 1] as const;

interface RevealProps {
  children: ReactNode;
  /** Seconds. Use to stagger siblings by hand where `Stagger` doesn't fit. */
  delay?: number;
  /** Animate when scrolled into view rather than on mount. */
  whileInView?: boolean;
  className?: string;
}

export function Reveal({
  children,
  delay = 0,
  whileInView = false,
  className,
}: RevealProps) {
  const reduceMotion = useReducedMotion();

  const hidden = reduceMotion ? { opacity: 0 } : { opacity: 0, y: 12 };
  const shown = { opacity: 1, y: 0 };

  const transition = {
    duration: reduceMotion ? 0.15 : 0.5,
    delay: reduceMotion ? 0 : delay,
    ease: EASE,
  };

  if (whileInView) {
    return (
      <motion.div
        className={className}
        initial={hidden}
        whileInView={shown}
        // `once` matters: re-animating on every scroll past is the single most
        // irritating scroll effect there is.
        viewport={{ once: true, amount: 0.25 }}
        transition={transition}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      className={className}
      initial={hidden}
      animate={shown}
      transition={transition}
    >
      {children}
    </motion.div>
  );
}

/**
 * Staggers direct children. Children must be `Stagger.Item` (or any motion
 * element that reads the `hidden`/`shown` variants) for the parent's
 * orchestration to reach them.
 */
export function Stagger({
  children,
  gap = 0.06,
  className,
}: {
  children: ReactNode;
  /** Seconds between each child. */
  gap?: number;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial="hidden"
      animate="shown"
      variants={{
        hidden: {},
        shown: {
          transition: { staggerChildren: reduceMotion ? 0 : gap },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      className={className}
      variants={{
        hidden: reduceMotion ? { opacity: 0 } : { opacity: 0, y: 10 },
        shown: {
          opacity: 1,
          y: 0,
          transition: { duration: reduceMotion ? 0.15 : 0.45, ease: EASE },
        },
      }}
    >
      {children}
    </motion.div>
  );
}
